<template>

    <el-card class="card-container">
      <template #header>
        <div class="card-header">
          <span>Report a Bug</span>
        </div>
      </template>
      <el-form
        :model="form"
        :rules="rules"
        ref="bugReportForm"
        label-position="top"
        class="preorder-form"
        v-loading="isSubmittingForm"
        element-loading-text="Submitting your bug report..."
      >
        <el-row :gutter="20" class="mt-3">
          <el-col :lg="12" :sm="24">
            <el-form-item label="First Name" prop="firstName">
              <el-input
                @update:model-value="value => form.firstName = value"
                :model-value="form.firstName"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :lg="12" :sm="24">
            <el-form-item label="Last Name" prop="lastName">
              <el-input
                @update:model-value="value => form.lastName = value"
                :model-value="form.lastName"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20" class="mt-3">
          <el-col :lg="12" :sm="24">
            <el-form-item label="Email" prop="email">
              <el-input v-model="form.email"></el-input>
            </el-form-item>
          </el-col>
          <el-col :lg="12" :sm="24">
            <el-form-item label="Application Type" prop="appType">
              <el-select
              :model-value="form.appType"
                  @update:model-value="value => form.appType = value"
                placeholder="Select application type"
                class="w-100"
              >
                <el-option label="Phone App" value="phone"></el-option>
                <el-option label="Web App" value="web"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20" class="mt-3">
          <el-col :lg="12" :sm="24">
            <el-form-item label="Bug Category" prop="category">
              <el-select
              :model-value="form.category"
                  @update:model-value="value => form.category = value"
                placeholder="Select a category"
                class="w-100"
              >
                <el-option label="UI Issue" value="ui"></el-option>
                <el-option
                  label="Performance Issue"
                  value="performance"
                ></el-option>
                <el-option label="Functional Bug" value="functional"></el-option>
                <el-option label="Hardware Issue" value="hardware"></el-option>
                <el-option label="Other" value="other"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :lg="12" :sm="24">
            <el-form-item label="Build Version" prop="buildVersion">
              <el-input
                @update:model-value="value => form.buildVersion = value"
                :model-value="form.buildVersion"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="Summary" prop="summary">
          <el-input @update:model-value="value => form.summary = value"
                :model-value="form.summary"></el-input>
        </el-form-item>

        <el-form-item label="Description" prop="description">
          <el-input type="textarea" @update:model-value="value => form.description = value"
                :model-value="form.description"></el-input>
        </el-form-item>
        <el-form-item label="Attachments">
          <el-upload action="#" list-type="picture-card" :auto-upload="false" v-model:file-list="uploadedFiles"
          accept="image/png, image/jpeg, video/mp4, video/avi, video/quicktime"
          :on-Change="handleFileChange"
       :multiple="true">
            <el-icon><Plus /></el-icon>

            <template #file="{ file }">
              <div>
                <img
                  class="el-upload-list__item-thumbnail"
                  :src="file.url"
                  alt=""
                />
                <span class="el-upload-list__item-actions">
                  <span
                    class="el-upload-list__item-preview white-text"
                    @click="handlePictureCardPreview(file)"
                  >
                    <el-icon><zoom-in /></el-icon>
                  </span>
                  <span
                    class="el-upload-list__item-delete"
                    @click="handleRemove(file)"
                  >
                    <el-icon><Delete /></el-icon>
                  </span>
                </span>
              </div>
            </template>
            <template #tip>
            <div class="el-upload__tip">
              Supported formats: PNG, JPEG, MP4, AVI, QuickTime. Maximum file size: 5MB.
            </div>
          </template>
          </el-upload>
        </el-form-item>
        
        <el-form-item>
          <el-button :loading="isSubmittingForm" @click="submitForm">Submit Bug Report</el-button>
        </el-form-item>

        <el-alert class="mb-2" v-if="dbError" :title="dbError" type="error" show-icon :closable="true" />
      <el-alert class="mb-2" v-if="uploadError" :title="uploadError" type="error" show-icon :closable="true" />
      <el-alert class="mb-2" v-if="formSubmitted"  title="Bug Report Submitted Successfully" type="success" show-icon :closable="true" />
      </el-form>

      <el-dialog :loading="isSubmittingForm" :model-value="dialogVisible" class="preview-dialog" @close="dialogVisible = false">
            <img :src="dialogImageUrl" alt="Preview Image" class="preview-image" />
            </el-dialog>

    </el-card>
  </template>

<script>
import { xray } from '../../config/pluginInit'
import { ref } from 'vue'
import useCollection from '../../services/useCollection'
import useStorage from '../../services/useStorage'
import firebase from '../../../firebase'
import { format } from 'date-fns'
import { Timestamp } from 'firebase/firestore'
import { ElMessage } from 'element-plus'

export default {
  name: 'ReportBugs',
  mounted () {
    xray.index()
  },
  setup () {
    const dialogImageUrl = ref('')
    const dialogVisible = ref(false)
    const uploadedFiles = ref([])
    const currentUser = firebase.firebaseAuth.currentUser
    const { addDocToDb, dbError } = useCollection('bugReports')
    const { uploadImageFile, uploadError } = useStorage()
    const isSubmittingForm = ref(false)
    const formSubmitted = ref(false)

    const handleRemove = (file) => {
      const index = uploadedFiles.value.findIndex(f => f.uid === file.uid)
      if (index !== -1) {
        uploadedFiles.value.splice(index, 1)
      }
    }

    const handlePictureCardPreview = (file) => {
      dialogImageUrl.value = file.url
      dialogVisible.value = true
    }

    const form = ref({
      firstName: currentUser.displayName ? currentUser.displayName.split(' ')[0] : '',
      lastName: currentUser.displayName && currentUser.displayName.split(' ').length > 1 ? currentUser.displayName.split(' ')[1] : '',
      email: currentUser.email,
      summary: '',
      category: '',
      buildVersion: '',
      description: '',
      appType: ''
    })

    const bugReportForm = ref(null)

    const rules = {
      firstName: [
        { required: true, message: 'Please enter your name', trigger: 'blur' }
      ],
      lastName: [
        { required: true, message: 'Please enter your name', trigger: 'blur' }
      ],
      email: [
        { required: true, message: 'Please enter your email', trigger: 'blur' },
        { type: 'email', message: 'Please enter a valid email', trigger: 'blur' }
      ],
      summary: [
        { required: true, message: 'Please enter a summary', trigger: 'blur' }
      ],
      description: [
        { required: true, message: 'Please enter your message', trigger: 'blur' }
      ],
      appType: [
        { required: true, message: 'Please select an application type', trigger: 'blur' }
      ]
    }

    const handleFileChange = (file, fileList) => {
      // Check size of file exceeds 5MB
      if (file.size > 5 * 1024 * 1024) {
        fileList.pop()
        ElMessage.error('File size exceeds 5MB')
      }
    }

    const submitForm = () => {
      bugReportForm.value.validate(async (valid) => {
        if (valid) {
          isSubmittingForm.value = true
          formSubmitted.value = false
          try {
            const promises = uploadedFiles.value.map(async (file, index) => {
            // Generate a unique file name: userID_timestamp_index
              const timestamp = format(new Date(), 'yyyyMMddHHmmss')
              const userId = currentUser.uid
              const filename = `${userId}_${timestamp}_${index}`

              const url = await uploadImageFile(file.raw, `bugReports/${filename}`)
              return url
            })

            const screenshots = await Promise.all(promises)

            const reportData = {
              ...form.value,
              screenshots
            }
            reportData.boardId = null
            reportData.createdAt = Timestamp.now()
            reportData.priority = 0
            await addDocToDb(reportData)
            // Reset the form and uploaded files list after submission
            bugReportForm.value.resetFields()
            uploadedFiles.value = []
            formSubmitted.value = true
          } catch (error) {
            console.error('Error submitting bug report:', error)
          } finally {
            isSubmittingForm.value = false
          }
        }
      })
    }

    return {
      form,
      rules,
      bugReportForm,
      dialogImageUrl,
      dialogVisible,
      handleRemove,
      handlePictureCardPreview,
      uploadedFiles,
      submitForm,
      isSubmittingForm,
      dbError,
      uploadError,
      formSubmitted,
      handleFileChange
        }
  }
}
</script>

  <style scoped>
  .card-container {
    margin: 0 20px 20px 20px;
  }

  .card-header {
    background: transparent;
    border-bottom: none;
  }

  .card-header span {
    font-size: 16px;
    font-weight: 500;
    color: var(--text-color) !important;
  }

.preview-image {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
}

  </style>

  <style>
  .el-upload-list {
    margin-bottom: 0px;
  }
  </style>
